import { FiLayers } from 'react-icons/fi';

export const sentenceClausesData = {
  courseId: 'sentence-clauses',
  courseType: 'grammar',
  title: 'Sentence Clauses',
  icon: FiLayers,
  overview: {
    impact: 'Shows grammatical range for Band 7+',
    focus: 'Understanding clause types',
    uses: 'Complex sentence construction',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Independent vs Dependent Clauses',
      content: `<p>Understanding clause types is key to writing complex sentences that score well in IELTS.</p>`,
      points: [
        'Independent clause: Can stand alone as a sentence. "The economy is growing."',
        'Dependent clause: Cannot stand alone. "Although the economy is growing..."',
        'Complex sentence = Independent + Dependent clause',
      ],
    },
    {
      title: 'Types of Dependent Clauses',
      content: `<p>There are three main types of dependent clauses:</p>`,
      table: {
        headers: ['Type', 'Function', 'Example'],
        rows: [
          ['Noun clause', 'Acts as a noun', 'What you said is true.'],
          ['Adjective clause', 'Describes a noun', 'The book that I read was excellent.'],
          ['Adverb clause', 'Modifies verb/sentence', 'Although it rained, we went out.'],
        ],
      },
    },
    {
      title: 'Common Clause Connectors',
      content: `<p>Use these words to create complex sentences:</p>`,
      points: [
        'Time: when, while, after, before, as soon as, until',
        'Reason: because, since, as',
        'Contrast: although, even though, while, whereas',
        'Condition: if, unless, provided that',
        'Purpose: so that, in order that',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify Clause Types',
      type: 'select',
      instructions: 'Identify the type of underlined clause.',
      questions: [
        {
          text: '"Although technology has benefits," it also creates challenges.',
          options: ['Noun clause', 'Adjective clause', 'Adverb clause'],
          answer: 'Adverb clause',
          explanation: '"Although" introduces a contrast adverb clause.',
        },
        {
          text: 'I believe "that education is important."',
          options: ['Noun clause', 'Adjective clause', 'Adverb clause'],
          answer: 'Noun clause',
          explanation: 'The clause functions as the object of "believe".',
        },
        {
          text: 'The student "who studies hard" will succeed.',
          options: ['Noun clause', 'Adjective clause', 'Adverb clause'],
          answer: 'Adjective clause',
          explanation: 'The clause describes "student" (a noun).',
        },
      ],
    },
    {
      title: 'Identify the Clause Type',
      type: 'select',
      instructions: 'Identify what type of clause is highlighted in each sentence.',
      questions: [
        {
          text: '"Because it was raining," we stayed inside.',
          options: ['Main clause', 'Subordinate (adverb) clause', 'Relative clause'],
          answer: 'Subordinate (adverb) clause',
          explanation: '"Because" introduces a reason clause, which is a subordinate adverb clause modifying the main clause.',
        },
        {
          text: 'The woman "who lives next door" is a doctor.',
          options: ['Main clause', 'Subordinate (adverb) clause', 'Relative clause'],
          answer: 'Relative clause',
          explanation: '"Who lives next door" is a relative clause describing "the woman".',
        },
        {
          text: '"We stayed inside" because it was raining.',
          options: ['Main clause', 'Subordinate (adverb) clause', 'Relative clause'],
          answer: 'Main clause',
          explanation: '"We stayed inside" can stand alone as a complete sentence — it is the main (independent) clause.',
        },
        {
          text: 'I don\'t know "what she wants."',
          options: ['Noun clause', 'Relative clause', 'Adverb clause'],
          answer: 'Noun clause',
          explanation: '"What she wants" acts as the object of "know", functioning as a noun clause.',
        },
      ],
    },
    {
      title: 'Combine Using Clauses',
      type: 'fill-in',
      instructions: 'Fill in the correct relative pronoun or connector (who, which, that, where, when) to combine the ideas.',
      questions: [
        {
          text: 'The book ___ I borrowed from the library was fascinating.',
          answer: 'that',
          explanation: '"That" (or "which") introduces a relative clause describing "the book".',
        },
        {
          text: 'She moved to Paris, ___ she studied art for three years.',
          answer: 'where',
          explanation: '"Where" introduces a relative clause referring to a place (Paris).',
        },
        {
          text: 'I remember the day ___ we first met.',
          answer: 'when',
          explanation: '"When" introduces a relative clause referring to a time (the day).',
        },
        {
          text: 'The teacher ___ taught us grammar was very patient.',
          answer: 'who',
          explanation: '"Who" introduces a relative clause referring to a person (the teacher).',
        },
      ],
    },
    {
      title: 'Write Complex Sentences',
      type: 'writing',
      instructions: 'Combine each pair of simple sentences into one complex sentence using a subordinate clause.',
      questions: [
        {
          text: 'The weather was cold. We decided to stay home.',
          sampleAnswer: 'Because the weather was cold, we decided to stay home.',
          explanation: 'Use a subordinating conjunction like "because", "since", or "as" to create an adverb clause showing reason.',
        },
        {
          text: 'She finished her degree. She got a promotion at work.',
          sampleAnswer: 'After she finished her degree, she got a promotion at work.',
          explanation: 'Use a time conjunction like "after", "when", or "once" to create a subordinate clause showing sequence.',
        },
        {
          text: 'The city has many parks. I grew up in that city.',
          sampleAnswer: 'The city where I grew up has many parks.',
          explanation: 'Use the relative pronoun "where" to combine the sentences, creating a relative clause that describes "the city".',
        },
      ],
    },
  ],
  tips: [
    'Use a variety of clause types for grammatical range',
    'Balance simple and complex sentences',
    'Practice combining clauses smoothly',
  ],
  dailyPractice: [
    'Identify clauses in reading passages',
    'Combine simple sentences using connectors',
    'Write paragraphs with varied clause structures',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Sentence Clauses: Understanding independent and dependent clauses, including noun, adjective, and adverb clauses for complex sentence construction.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...sentenceClausesData,
      title: sentenceClausesData.title + ' (Foundations)',
      overview: {
        ...sentenceClausesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: sentenceClausesData.sections.slice(0, Math.max(2, Math.ceil(sentenceClausesData.sections.length / 2))),
      exercises: sentenceClausesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...sentenceClausesData,
      title: sentenceClausesData.title + ' (Advanced)',
      overview: {
        ...sentenceClausesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: sentenceClausesData.sections,
      exercises: sentenceClausesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(sentenceClausesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return sentenceClausesData;
}
